import { useEffect } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";

import { getContent } from "lib/content/get-content";
import type { ContentNode, Part } from "lib/types";

export const CHUNK_SIZE = 5;

/**
 * Индексы частей, не вошедших в начальный буфер.
 * Используется и для ключей загрузки, и для сборки контента.
 */
export function getRemainingPartIndices(
  totalParts: number,
  bufferStart: number,
  bufferEnd: number
): number[] {
  const indices: number[] = [];
  for (let i = 0; i < totalParts; i++) {
    if (i < bufferStart || i > bufferEnd) {
      indices.push(i);
    }
  }
  return indices;
}

/**
 * Собирает полный массив контента из начальных частей и загруженных чанков.
 * Возвращает sparse-массив: загруженные позиции заполнены, остальные — undefined.
 */
export function assembleContent(
  totalParts: number,
  initialParts: ContentNode[][],
  bufferStart: number,
  loadedChunks: Map<number, ContentNode[][]>,
  remainingIndices: number[]
): (ContentNode[] | undefined)[] {
  const result: (ContentNode[] | undefined)[] = new Array(totalParts);

  for (let i = 0; i < initialParts.length; i++) {
    result[bufferStart + i] = initialParts[i];
  }

  for (const [chunkIndex, chunk] of loadedChunks) {
    const startIdx = chunkIndex * CHUNK_SIZE;
    for (let i = 0; i < chunk.length; i++) {
      const idx = startIdx + i;
      if (idx < remainingIndices.length) {
        result[remainingIndices[idx]] = chunk[i];
      }
    }
  }

  return result;
}

/** Определяет, какому чанку принадлежит данная часть */
export function getChunkForPart(
  partIndex: number,
  remainingIndices: number[]
): number | null {
  const idx = remainingIndices.indexOf(partIndex);
  if (idx === -1) return null;
  return Math.floor(idx / CHUNK_SIZE);
}

interface ProgressiveContentParams {
  art: number;
  remainingParts: Part[];
  remainingIndices: number[];
  totalParts: number;
  initialParts: ContentNode[][];
  bufferStart: number;
}

export function useProgressiveContent({
  art,
  remainingParts,
  remainingIndices,
  totalParts,
  initialParts,
  bufferStart,
}: ProgressiveContentParams) {
  const totalChunks = Math.ceil(remainingParts.length / CHUNK_SIZE);

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: ["content-remaining", art],
      queryFn: async ({ pageParam }) => {
        const start = pageParam * CHUNK_SIZE;
        const chunkParts = remainingParts.slice(start, start + CHUNK_SIZE);
        const results = await getContent(chunkParts);
        return results.map(r =>
          Array.isArray(r) ? r : [r]
        ) as ContentNode[][];
      },
      initialPageParam: 0,
      getNextPageParam: (_lastPage, allPages) => {
        const nextIndex = allPages.length;
        return nextIndex < totalChunks ? nextIndex : undefined;
      },
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      enabled: totalChunks > 0,
    });

  // Фоновая последовательная загрузка: когда текущие чанки загрузились, запрашиваем следующий
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Собираем загруженные чанки в Map для assembleContent
  const loadedChunks = new Map<number, ContentNode[][]>();
  if (data) {
    for (let i = 0; i < data.pages.length; i++) {
      if (data.pages[i]) {
        loadedChunks.set(i, data.pages[i]);
      }
    }
  }

  const content = assembleContent(
    totalParts,
    initialParts,
    bufferStart,
    loadedChunks,
    remainingIndices
  );

  const loadedCount = content.filter(Boolean).length;

  function requestPart(partIndex: number) {
    const chunkIndex = getChunkForPart(partIndex, remainingIndices);
    if (chunkIndex !== null && data && chunkIndex >= data.pages.length) {
      fetchNextPage();
    }
  }

  return {
    content: content as ContentNode[][],
    loadedCount,
    isFullyLoaded: totalParts > 0 && loadedCount === totalParts,
    requestPart,
  };
}
